import { Component, createRef } from 'preact';
import { toast } from 'sonner';
import { ExternalLink } from 'lucide-react';
import RuneSpinner from './RuneSpinner';
import { fetchCosmetics, buyCosmeticPack } from '../utils/arena/cosmeticsApi';
import { noteCosmeticUnlocked, invalidateCosmeticsCache } from '../utils/arena/cosmeticsCache';
import { playUI, UI } from '../utils/arena/uiSounds';
import { CoinIcon } from './ui/icons';
import {
  GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, ACCENT_GOLD,
  GOLD_BTN, BEVELED_BTN, PANEL_BG, PANEL_BORDER,
  CornerPlating, FourCorners,
} from '../lib/medievalTheme';
import VikingOrnament from './VikingOrnament';
import ForgedButton from './ui/ForgedButton';
import { TabBar } from './ui/TabBar';
import { getLocalApiOrigin } from '../utils/localApi';
import { t } from '../localization/t';
import DiceSkinShelf from './DiceSkinShelf';
import {
  getPackPool, getPlaymatPreviewUrl, getTokenPreviewUrl, pickStablePreviewCards,
  RealmStackPreview, PlaymatPreview, TokenCardPreview, PortraitFanPreview,
  DiceSetPreview, CosmeticRevealModal,
  REALM_PILE_LAYERS, PORTRAIT_FAN_LAYERS,
} from './cosmeticPackPreview';

// Gold medallion seals (bundled under public/playmats/seals/). All
// current protected mats are Collector Arthouse / Séverine Pineaux; the
// sidebar shows both. If more houses/artists are added later, drive
// these from catalog fields instead of the constants here.
const SEAL_BASE = `${getLocalApiOrigin()}/game-assets/playmats/seals`;
const COLLECTOR_ARTHOUSE_SEAL = `${SEAL_BASE}/collector-arthouse.webp`;
const SEVERINE_SEAL = `${SEAL_BASE}/severine-pineaux.webp`;

// Synthetic pack for the free Vanilla mat in the onboarding starter
// picker. Vanilla isn't a server cosmetic pack (it's the free default,
// no entitlement), so it's injected client-side only in starterPick
// mode. `cosmeticId: null` → claiming it grants nothing, just sets the
// theme + consumes the one free pick.
const STARTER_VANILLA_PACK = {
  id: 'pack-playmat-vanilla',
  kind: 'playmat',
  cosmeticId: null,
  themeId: 'valkenhall-vanilla',
  name: 'Valkenhall Marble Playmat',
  description: 'The classic Valkenhall battlefield. Free for every sorcerer.',
  lore: 'An ordinary site of polished marble.',
  previewFile: 'art-only/vanilla-valkenhall-playmat.webp',
  price: { coins: 0 },
};

// Shared medieval surface: tiled noise + stone over a warm-dark base,
// gold hairline, inset highlight. Matches the store tab bar so the
// owned stamp + name plate read as crafted plates, not flat chips.
const PLATE_BG = `url("/tex-noise.webp"), url("/tex-stone.webp"), linear-gradient(180deg, rgba(20,15,9,0.96) 0%, rgba(10,7,4,0.96) 100%)`;

// "Owned" seal. A textured gold-rimmed plate with a beveled edge and
// verdigris-green lettering. Used at tile scale (grid) and panel scale
// (sidebar); callers set the font size + padding.
const OWNED_STAMP_STYLE = {
 color: '#8fe07a',
 fontWeight: 800,
 background: PLATE_BG,
 backgroundBlendMode: 'overlay, normal, normal',
 border: `1px solid ${GOLD} 0.5)`,
  borderRadius: 'var(--radius-sm)',
  boxShadow: 'inset 0 1px 0 rgba(255,220,140,0.2), inset 0 -1px 2px rgba(0,0,0,0.55), 0 2px 6px rgba(0,0,0,0.6)',
  textShadow: '0 1px 2px rgba(0,0,0,0.9)',
};

// One-shot stylesheet for the seal-credit buttons. Hoisted out of the
// per-render path so we don't re-create a <style> DOM node every time
// the sidebar swaps to a different playmat. Pseudo-classes (:hover,
// :active) can't be expressed in React/Preact inline style objects, so
// a real CSS rule is the only option.
if (typeof document !== 'undefined' && !document.getElementById('cosmetics-seal-btn-style')) {
  const styleEl = document.createElement('style');
  styleEl.id = 'cosmetics-seal-btn-style';
  styleEl.textContent = `
    .seal-credit-btn { transition: transform 0.12s ease-out, box-shadow 0.12s ease-out, background 0.12s ease-out, border-color 0.12s ease-out; }
    .seal-credit-btn:hover { transform: translateY(-1px); background: linear-gradient(180deg, rgba(58,42,22,0.95) 0%, rgba(40,28,16,0.95) 100%) !important; border-color: rgba(212,168,67,0.6) !important; box-shadow: inset 0 1px 0 rgba(255,220,140,0.14), 0 4px 12px rgba(0,0,0,0.5) !important; }
    .seal-credit-btn:active { transform: translateY(0); box-shadow: inset 0 2px 4px rgba(0,0,0,0.45) !important; }
  `;
  document.head.appendChild(styleEl);
}

// Scales its children's font down until they fit the box's height, so a
// long lore + artist bio is always shown in full without the sidebar
// ever scrolling. Children opt in by sizing their text against the
// `--fit-scale` CSS var (e.g. `calc(0.7cqw * var(--fit-scale, 1))`); the
// whole block scales by one factor so the lore/bio proportions stay
// consistent within a card. Re-fits on container resize and whenever
// `signature` changes (i.e. a different playmat is selected).
const MIN_FIT_SCALE = 0.5;

class AutoFitText extends Component {
  rootRef = createRef();
  innerRef = createRef();

  componentDidMount() {
    if (typeof ResizeObserver !== 'undefined' && this.rootRef.current) {
      this.ro = new ResizeObserver(() => this.fit());
      this.ro.observe(this.rootRef.current);
    }
    this.fit();
  }

  componentDidUpdate(prev) {
    if (prev.signature !== this.props.signature) this.fit();
  }

  componentWillUnmount() {
    this.ro?.disconnect();
  }

  // Measure the natural (unscaled) content height against the available
  // box height and pick a scale that fits. Text reflows as it shrinks, so
  // after the first estimate we tighten in small steps until the content
  // no longer overflows (or we hit the readability floor).
  fit = () => {
    const root = this.rootRef.current;
    const inner = this.innerRef.current;
    if (!root || !inner) return;
    root.style.setProperty('--fit-scale', '1');
    const avail = root.clientHeight;
    if (!avail) return;
    if (inner.scrollHeight <= avail) return;
    let scale = avail / inner.scrollHeight;
    root.style.setProperty('--fit-scale', String(scale));
    for (let i = 0; i < 8 && inner.scrollHeight > avail && scale > MIN_FIT_SCALE; i++) {
      scale = Math.max(MIN_FIT_SCALE, scale * 0.96);
      root.style.setProperty('--fit-scale', String(scale));
    }
  };

  render() {
    return (
      <div ref={this.rootRef} className={this.props.className}>
        <div ref={this.innerRef} className="flex flex-col gap-3 w-full">
          {this.props.children}
        </div>
      </div>
    );
  }
}

export default class StoreCosmeticsTab extends Component {
  state = {
    loading: true,
    error: null,
    packs: [],
    owned: new Set(),
    // Per-pack qty in cart, keyed by pack.id
    cart: {},
    // Stable preview card list per pack. Picked once on load. Stored
    // as an array so we can render multi-card stacks/fans.
    previewByPack: {},
    // ID of the pack currently being hovered, so its preview can fan
    // out and float above its neighbors.
    hoveredPackId: null,
    subTab: 'portrait',
    // Playmat sub-tab uses a Spellcraft-style 65/35 layout: a grid of
    // playmat tiles on the left, an artwork preview + buy panel on
    // the right. This holds the pack.id of the currently previewed
    // mat (or null on first entry, until a tile is clicked).
    selectedPlaymatId: null,
    showConfirm: false,
    buying: false,
    // Array of { pack, card } pairs revealed after a successful
    // multi-pack purchase, or null when no reveal is active.
    reveal: null,
  };

  componentDidMount() {
    this.load();
  }

  load = async () => {
    this.setState({ loading: true, error: null });
    try {
      const { packs, owned } = await fetchCosmetics();
      const sorceryCards = this.props.sorceryCards;
      const previewByPack = {};
      for (const pack of packs || []) {
        // Playmat, token and dice packs render their own art (no card
        // stack), so we skip the preview-card picker for them.
        if (pack.kind === 'playmat' || pack.kind === 'token' || pack.kind === 'dice') {
          previewByPack[pack.id] = [];
          continue;
        }
        const n = pack.kind === 'background' ? REALM_PILE_LAYERS.length : PORTRAIT_FAN_LAYERS.length;
        previewByPack[pack.id] = pickStablePreviewCards(getPackPool(sorceryCards, pack), n);
      }
      this.setState({
        packs: Array.isArray(packs) ? packs : [],
        owned: new Set(Array.isArray(owned) ? owned : []),
        previewByPack,
        loading: false,
      });
    } catch (err) {
      console.error('[StoreCosmeticsTab] Failed to load:', err);
      this.setState({ loading: false, error: t('Failed to load cosmetics.') });
    }
  };

  remainingForPack = (pack) => {
    // Playmats, tokens and dice skins are FIXED-entitlement packs. No
    // random card pool, just the one cosmeticId. The card-pool math below
    // returns 0 for them because getPackPool only matches sorcery
    // card subtypes; without this short-circuit, remainingForPack
    // reports 0 and the +/- controls / addToCart silently refuse to
    // accept the playmat. Was a pre-existing bug that prevented
    // playmat purchases via the cart UI altogether.
    if (pack.kind === 'playmat' || pack.kind === 'token' || pack.kind === 'dice') {
      if (pack.cosmeticId && this.state.owned.has(pack.cosmeticId)) return 0;
      return 1;
    }
    const pool = getPackPool(this.props.sorceryCards, pack);
    const ownedInPool = pool.filter((c) => this.state.owned.has(c.unique_id)).length;
    return Math.max(0, pool.length - ownedInPool);
  };

  setCart = (packId, value) => {
    const pack = this.state.packs.find((p) => p.id === packId);
    if (!pack) return;
    const max = this.remainingForPack(pack);
    const qty = Math.max(0, Math.min(max, parseInt(value, 10) || 0));
    this.setState((s) => ({ cart: { ...s.cart, [packId]: qty } }));
  };

  addToCart = (packId) => {
    const pack = this.state.packs.find((p) => p.id === packId);
    if (!pack) return;
    const remaining = this.remainingForPack(pack);
    this.setState((s) => {
      const current = s.cart[packId] || 0;
      if (current >= remaining) return null;
      playUI(UI.CLICK);
      return { cart: { ...s.cart, [packId]: current + 1 } };
    });
  };

  getCartTotal() {
    const { cart, packs } = this.state;
    let count = 0;
    let cost = 0;
    for (const pack of packs) {
      const qty = cart[pack.id] || 0;
      if (qty === 0) continue;
      count += qty;
      cost += qty * (pack.price?.coins || 0);
    }
    return { count, cost };
  }

  rollUnopenedCard = (pack, ownedSet) => {
    // Playmats, tokens and dice skins skip the random-card roll: the server
    // grants pack.cosmeticId regardless of what the client sends, so
    // we return a synthetic "card" carrying the cosmeticId. Same
    // pre-existing bug as in remainingForPack. Without this, the
    // purchase handler silently produced no rolls for playmats and
    // bailed with "Nothing left to unlock".
    if (pack.kind === 'playmat' || pack.kind === 'token' || pack.kind === 'dice') {
      if (pack.cosmeticId && ownedSet.has(pack.cosmeticId)) return null;
      return { unique_id: pack.cosmeticId || pack.id, name: pack.name };
    }
    const pool = getPackPool(this.props.sorceryCards, pack).filter((c) => !ownedSet.has(c.unique_id));
    if (pool.length === 0) return null;
    return pool[Math.floor(Math.random() * pool.length)];
  };

  handlePurchase = async () => {
    if (this.state.buying) return;
    const { cart, packs } = this.state;
    const cartEntries = packs
      .map((p) => ({ pack: p, qty: cart[p.id] || 0 }))
      .filter((e) => e.qty > 0);
    if (cartEntries.length === 0) return;

    // Roll cards client-side, simulating the running owned-set so
    // multi-quantity purchases never duplicate within a single
    // transaction.
    const simulated = new Set(this.state.owned);
    const rolls = [];
    for (const { pack, qty } of cartEntries) {
      for (let i = 0; i < qty; i++) {
        const card = this.rollUnopenedCard(pack, simulated);
        if (!card) break;
        simulated.add(card.unique_id);
        rolls.push({ pack, card });
      }
    }
    if (rolls.length === 0) {
      toast.error(t('Nothing left to unlock in those packs.'));
      this.setState({ showConfirm: false });
      return;
    }

    this.setState({ buying: true, showConfirm: false });
    playUI(UI.GOLD);

    // Commit each roll sequentially. We collect successes and report
    // failures via toast; the reveal modal shows whatever made it
    // through.
    const unlocked = [];
    for (const { pack, card } of rolls) {
      try {
        await buyCosmeticPack(pack.id, card.unique_id);
        unlocked.push({ pack, card });
        noteCosmeticUnlocked(card.unique_id);
        this.props.onAfterPurchase?.(card.unique_id);
      } catch (err) {
        const reason = err?.reason || 'unknown';
        const msgByReason = {
          'already-owned': t('{name} was already unlocked. Skipped.', { name: card.name }),
          'insufficient-coins': t('Ran out of gold partway through the cart.'),
          'insufficient-keystones': t('Not enough keystones.'),
          'insufficient-arcana': t('Not enough arcana shards.'),
          'pack-not-found': t('{name} is no longer available.', { name: pack.name }),
        };
        toast.error(msgByReason[reason] || t('Failed to open {name}.', { name: pack.name }));
        if (reason === 'insufficient-coins' || reason === 'insufficient-keystones' || reason === 'insufficient-arcana') {
          break;
        }
      }
    }

    // Sync our owned set + clear the cart. If anything went sideways
    // (race condition on ownership), force a reload so the next render
    // matches the server's view.
    const nextOwned = new Set(this.state.owned);
    for (const { card } of unlocked) nextOwned.add(card.unique_id);
    this.setState({
      owned: nextOwned,
      cart: {},
      buying: false,
      reveal: unlocked.length > 0 ? unlocked : null,
    });
    if (unlocked.length !== rolls.length) {
      invalidateCosmeticsCache();
      this.load();
    } else if (unlocked.length > 0) {
      playUI(UI.CONFIRM);
    }
  };

  renderPackTile = (pack) => {
    const { previewByPack, cart, owned, buying, hoveredPackId } = this.state;
    const pool = getPackPool(this.props.sorceryCards, pack);
    const totalInPool = pool.length;
    const ownedInPool = pool.filter((c) => owned.has(c.unique_id)).length;
    const remaining = totalInPool - ownedInPool;
    const exhausted = totalInPool > 0 && remaining === 0;
    const qty = cart[pack.id] || 0;
    const previewCards = previewByPack[pack.id] || [];
    const hasPreview = previewCards.length > 0;
    const isPlaymat = pack.kind === 'playmat';
    const isToken = pack.kind === 'token';
    const isDice = pack.kind === 'dice';
    const isLandscape = pack.kind === 'background' || isPlaymat;
    const isHovered = hoveredPackId === pack.id && !exhausted;
    const playmatPreviewUrl = isPlaymat ? getPlaymatPreviewUrl(pack) : null;
    const tokenPreviewUrl = isToken ? getTokenPreviewUrl(pack) : null;

    return (
      <div key={pack.id} className="flex flex-col items-center">
        <div
          className="relative"
          style={{
            ...(isLandscape
              ? { width: '19.55cqw', height: '14cqw' }
              : { width: '14cqw', height: '19.55cqw' }),
            // Hovered tile sits above its grid neighbors so the wider
            // fan isn't clipped by adjacent siblings.
            zIndex: isHovered ? 10 : 'auto',
          }}
        >
          <button
            type="button"
            className="relative block w-full h-full cursor-pointer disabled:cursor-not-allowed"
            style={{
              background: 'transparent',
              border: 'none',
              padding: 0,
              opacity: exhausted ? 0.55 : 1,
            }}
            onClick={() => !exhausted && !buying && this.addToCart(pack.id)}
            onMouseEnter={() => this.setState({ hoveredPackId: pack.id })}
            onMouseLeave={() => this.setState((s) => (s.hoveredPackId === pack.id ? { hoveredPackId: null } : null))}
            disabled={exhausted || buying}
            title={pack.name}
          >
            {isPlaymat ? (
              <PlaymatPreview url={playmatPreviewUrl} hovered={isHovered} />
            ) : isToken ? (
              <TokenCardPreview url={tokenPreviewUrl} hovered={isHovered} />
            ) : isDice ? (
              <DiceSetPreview pack={pack} hovered={isHovered} />
            ) : hasPreview ? (
              pack.kind === 'background'
                ? <RealmStackPreview cards={previewCards} hovered={isHovered} />
                : <PortraitFanPreview cards={previewCards} hovered={isHovered} />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[0.625cqw]" style={{ color: TEXT_MUTED }}>
                ?
              </div>
            )}
            {exhausted ? (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ zIndex: 20 }}>
                <span
                  className="text-[0.65cqw] font-bold arena-heading px-3 py-1"
                  style={{
                    color: '#7ad06a',
                    textShadow: '0 1px 3px rgba(0,0,0,0.95)',
                    background: 'rgba(8,6,4,0.85)',
                    borderRadius: 'var(--radius-sm)',
                  }}
                >
                  {isPlaymat || isToken ? t('Owned') : t('All Unlocked')}
                </span>
              </div>
            ) : null}
          </button>
          {qty > 0 ? (
            <div
              className="absolute -top-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-black"
              style={{ background: '#d4a843', boxShadow: '0 0 12px rgba(212,168,67,0.45), 0 2px 4px rgba(0,0,0,0.5)', zIndex: 30 }}
            >
              {qty}
            </div>
          ) : null}
        </div>

        <div
          className="text-[0.75cqw] font-bold arena-heading mt-3 px-2.5 py-1 max-w-[15cqw] truncate"
          style={{
            color: '#e8d5a0',
            textShadow: '0 2px 6px rgba(0,0,0,0.9)',
            background: 'linear-gradient(180deg, rgba(14,11,8,0.85) 0%, rgba(8,6,4,0.9) 100%)',
            border: `1px solid ${GOLD} 0.32)`,
            borderRadius: 'var(--radius-sm)',
            boxShadow: 'inset 0 1px 0 rgba(255,220,140,0.08), 0 2px 6px rgba(0,0,0,0.5)',
          }}
          title={pack.name}
        >
          {pack.name}
        </div>

        <div className="text-[0.55cqw] mt-1" style={{ color: exhausted ? '#7ad06a' : TEXT_MUTED }}>
          {isPlaymat && pack.artist && pack.artistUrl ? (
            <>
              {exhausted ? t('owned · art by') : t('art by')}{' '}
              <a
                href={pack.artistUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:opacity-80"
                style={{ color: 'inherit' }}
                onClick={(e) => e.stopPropagation()}
              >
                {pack.artist}
              </a>
            </>
          ) : isPlaymat ? (exhausted ? t('owned') : t('playmat skin'))
            : isToken ? (exhausted ? t('owned') : pack.artist ? t('art by {artist}', { artist: pack.artist }) : t('art by community'))
            : isDice ? (exhausted ? t('owned') : t('dice skin'))
            : t('{owned}/{total} owned', { owned: ownedInPool, total: totalInPool })}
        </div>

        <div className="flex items-center gap-1 mt-2">
          <button
            type="button"
            className="w-7 h-7 flex items-center justify-center text-sm font-bold transition-all hover:scale-110 active:scale-90 disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              ...BEVELED_BTN,
              background: `url("/tex-noise.webp"), url("/tex-stone.webp"), rgba(20, 16, 10, 0.95)`,
              borderRadius: 'var(--radius-sm)',
              color: '#A6A09B',
            }}
            disabled={qty === 0 || buying}
            onClick={() => this.setCart(pack.id, qty - 1)}
          >
            −
          </button>
          <input
            type="number"
            min="0"
            max={remaining}
            value={qty}
            className="w-9 h-7 text-center text-sm font-bold outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            style={{ background: PANEL_BG, border: `1px solid ${GOLD} 0.18)`, borderRadius: 'var(--radius-sm)', color: '#e8d5a0' }}
            onInput={(e) => this.setCart(pack.id, e.target.value)}
            disabled={exhausted || buying}
          />
          <button
            type="button"
            className="w-7 h-7 flex items-center justify-center text-sm font-bold transition-all hover:scale-110 active:scale-90 disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              ...BEVELED_BTN,
              background: `url("/tex-noise.webp"), url("/tex-stone.webp"), rgba(20, 16, 10, 0.95)`,
              borderRadius: 'var(--radius-sm)',
              color: '#A6A09B',
            }}
            disabled={qty >= remaining || buying}
            onClick={() => this.setCart(pack.id, qty + 1)}
          >
            +
          </button>
        </div>

        <div className="mt-1 text-[0.65cqw] tabular-nums inline-flex items-center gap-1" style={{ color: '#f0d060' }}>
          <CoinIcon size={11} />
          {pack.price?.coins || 0}
        </div>
      </div>
    );
  };

  renderSubTabs(portraitPacks, backgroundPacks, playmatPacks, tokenPacks, dicePacks) {
    const { subTab } = this.state;
    const tabs = [
      { id: 'portrait', label: t('Portrait Packs'), count: portraitPacks.length },
      { id: 'background', label: t('Background Packs'), count: backgroundPacks.length },
      { id: 'playmat', label: t('Playmats'), count: playmatPacks.length },
      { id: 'token', label: t('Tokens'), count: tokenPacks.length },
      { id: 'dice', label: t('Dice'), count: dicePacks.length },
    ].filter((t) => t.count > 0);
    if (tabs.length <= 1) return null;
    return (
      <div className="flex items-center justify-center pt-3 pb-1 shrink-0">
        <TabBar
          role="tablist"
          className="inline-flex items-center p-1"
          variant="plate"
          activeIndex={tabs.findIndex((st) => st.id === subTab)}
          indicatorStyle={{
            background: `url("/tex-noise.webp"), linear-gradient(180deg, rgba(212,168,67,0.18) 0%, rgba(180,140,55,0.10) 50%, rgba(120,85,30,0.14) 100%)`,
            border: `1px solid ${GOLD} 0.42)`,
            boxShadow: 'inset 0 1px 0 rgba(255,220,140,0.18), inset 0 -1px 0 rgba(0,0,0,0.35), 0 0 14px rgba(212,168,67,0.18)',
          }}
          style={{
            background: `url("/tex-noise.webp"), linear-gradient(180deg, rgba(6,5,3,0.92) 0%, rgba(12,9,5,0.88) 100%)`,
            border: `1px solid ${GOLD} 0.22)`,
            borderRadius: 'var(--radius-md)',
            boxShadow: 'inset 0 2px 6px rgba(0,0,0,0.55), inset 0 0 0 1px rgba(0,0,0,0.4), 0 1px 0 rgba(255,220,140,0.06)',
          }}
        >
          {tabs.map((st) => {
            const active = subTab === st.id;
            return (
              <button
                key={st.id}
                type="button"
                role="tab"
                data-tab-item
                aria-selected={active}
                className="px-7 py-2 text-[0.6875cqw] font-semibold"
                style={{
                  color: active ? '#f0d990' : 'rgba(166,160,155,0.55)',
                  background: 'transparent',
                  // Transparent rim keeps the label box identical to the
                  // indicator's, so the plate lands flush on it.
                  border: '1px solid transparent',
                  borderRadius: 'var(--radius-sm)',
                  textShadow: active
                    ? '0 0 10px rgba(232,213,160,0.35), 0 1px 2px rgba(0,0,0,0.7)'
                    : '0 1px 2px rgba(0,0,0,0.7)',
                }}
                onClick={() => this.handleSubTabChange(st.id)}
              >
                <span className="arena-heading">{st.label}</span>
              </button>
            );
          })}
        </TabBar>
      </div>
    );
  }

  // Switching sub-tabs auto-selects the first playmat when entering
  // the Playmats tab so the sidebar isn't sitting empty on first
  // open. Switching away clears the selection so re-entering re-runs
  // the auto-select (avoids stale highlight on a pack that's no
  // longer in the visible list).
  handleSubTabChange = (id) => {
    if (id === this.state.subTab) return;
    this.setState((s) => {
      const next = { subTab: id };
      if (id === 'playmat') {
        const playmats = s.packs
          .filter((p) => p.kind === 'playmat')
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        next.selectedPlaymatId = playmats[0]?.id || null;
      } else {
        next.selectedPlaymatId = null;
      }
      return next;
    });
    playUI(UI.CLICK);
  };

  handleSelectPlaymat = (packId) => {
    if (this.state.selectedPlaymatId === packId) return;
    this.setState({ selectedPlaymatId: packId });
    playUI(UI.CLICK);
  };

  // Sidebar Purchase button on the playmat tab. Adds the selected
  // playmat to cart at qty=1 (playmats are single entitlements. No
  // qty>1 makes sense) and opens the confirm modal. If the user
  // accumulated cart items on other sub-tabs earlier they'll see
  // them in the confirm modal alongside; that's the same multi-tab
  // cart behaviour the bottom bar always had.
  handleBuyPlaymat = (pack) => {
    if (this.state.buying) return;
    if (pack.cosmeticId && this.state.owned.has(pack.cosmeticId)) return;
    this.setState((s) => ({
      cart: { ...s.cart, [pack.id]: 1 },
      showConfirm: true,
    }));
    playUI(UI.CLICK);
  };

  // Single playmat tile for the Spellcraft-style grid in the
  // playmat sub-tab. Simpler than renderPackTile. No +/- or price
  // (those live in the sidebar), just the preview, name, selection
  // ring, and an owned/qty badge. Clicking selects the mat for the
  // sidebar preview.
  renderPlaymatGridTile = (pack) => {
    const { owned, buying, hoveredPackId, selectedPlaymatId } = this.state;
    // Playmats are single-entitlement (qty is always 0 or 1), so the
    // tile doesn't carry a qty badge or any +/- controls. Ownership
    // is the direct entitlement check via the cosmeticId.
    const ownsThis = pack.cosmeticId ? owned.has(pack.cosmeticId) : false;
    const isHovered = hoveredPackId === pack.id;
    const isSelected = selectedPlaymatId === pack.id;
    const url = getPlaymatPreviewUrl(pack);

    return (
      <button
        key={pack.id}
        type="button"
        className="relative block w-full transition-all"
        style={{
          aspectRatio: '14 / 10',
          background: 'transparent',
          border: 'none',
          padding: 0,
          // Selection ring sits on the button, OUTSIDE the clipped art
          // frame, so it's never cut off (the scroll container is padded
          // to give it room. See renderPlaymatLayout).
          outline: isSelected ? `0.18cqw solid ${ACCENT_GOLD}` : '0.18cqw solid transparent',
          outlineOffset: '0.2cqw',
          borderRadius: 'var(--radius-lg)',
          transform: isHovered && !isSelected ? 'translateY(-0.2cqw)' : 'none',
        }}
        onClick={() => this.handleSelectPlaymat(pack.id)}
        onMouseEnter={() => this.setState({ hoveredPackId: pack.id })}
        onMouseLeave={() => this.setState((s) => (s.hoveredPackId === pack.id ? { hoveredPackId: null } : null))}
        disabled={buying}
        title={pack.name}
      >
        {/* Art frame. Clips the art AND the overlays to one rounded
            card, so the owned stamp and name plate hug the real image
            edges in every state (the old layout pinned them to the
            button while the art floated at 92%, so they drifted). */}
        <div
          className="absolute inset-0 overflow-hidden"
          style={{
            borderRadius: 'var(--radius-lg)',
            border: `1px solid ${isSelected ? ACCENT_GOLD : `${GOLD} 0.22)`}`,
            boxShadow: isSelected
              ? `0 0 0 1px rgba(0,0,0,0.5), 0 10px 24px rgba(0,0,0,0.6), 0 0 18px ${GOLD} 0.2)`
              : '0 8px 20px rgba(0,0,0,0.55), 0 0 0 1px rgba(0,0,0,0.45)',
          }}
        >
          <PlaymatPreview url={url} hovered={isHovered || isSelected} fill />

          {ownsThis ? (
            <div
              className="absolute top-2 left-2 px-2 py-0.5 text-[0.55cqw]"
              style={{ ...OWNED_STAMP_STYLE, zIndex: 20 }}
            >
              {t('Owned')}
            </div>
          ) : null}

          {/* Name banner. Fades up from the bottom edge of the art so
              the title is legible over any artwork; flush left/right/
              bottom so its position is identical whether or not the
              tile is selected. */}
          <div
            className="absolute left-0 right-0 bottom-0 px-2.5 py-1.5 text-[0.72cqw] font-semibold arena-heading truncate text-center"
            style={{
              color: '#f0dca8',
              textShadow: '0 1px 3px rgba(0,0,0,0.95)',
              background: 'linear-gradient(180deg, rgba(10,7,4,0) 0%, rgba(10,7,4,0.72) 40%, rgba(8,6,4,0.94) 100%)',
              borderTop: `1px solid ${GOLD} 0.16)`,
            }}
          >
            {pack.name.replace(/ Playmat$/, '')}
          </div>
        </div>
      </button>
    );
  };

  // A gold-seal credit rendered as a full clickable button (medallion +
  // label + name). The whole surface is the click target with a hover
  // lift + press feedback (see `.seal-credit-btn`), so it reads as
  // interactive rather than a bare text link. Falls back to a static
  // div when there's no link.
  renderArtistSeal(sealUrl, label, name, href) {
    const inner = (
      <>
        <img
          src={sealUrl}
          alt=""
          draggable={false}
          style={{ width: '2.4cqw', height: '2.4cqw', objectFit: 'contain', filter: 'drop-shadow(0 2px 5px rgba(0,0,0,0.6))' }}
        />
        <div className="text-left leading-tight">
          <div className="text-[0.5cqw]" style={{ color: TEXT_MUTED }}>{label}</div>
          <div className="text-[0.74cqw] font-semibold arena-heading" style={{ color: ACCENT_GOLD }}>{name}</div>
        </div>
      </>
    );
    const baseStyle = {
      background: 'linear-gradient(180deg, rgba(40,30,18,0.92) 0%, rgba(28,20,12,0.92) 100%)',
      border: `1px solid ${GOLD} 0.3)`,
      borderRadius: 'var(--radius-md)',
      boxShadow: 'inset 0 1px 0 rgba(255,220,140,0.06), 0 2px 6px rgba(0,0,0,0.4)',
      textDecoration: 'none',
    };
    if (!href) {
      return <div className="flex items-center gap-2 px-2.5 py-1.5" style={baseStyle}>{inner}</div>;
    }
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="seal-credit-btn flex items-center gap-2 px-2.5 py-1.5"
        style={baseStyle}
      >
        {inner}
      </a>
    );
  }

  // Right-hand preview panel for the selected playmat. Mirrors the
  // Spellcraft sidebar (FourCorners + VikingOrnament backdrop).
  // Shows the artwork at the largest size that fits the panel, the
  // pack name + artist credit, description, price + qty controls,
  // and (for collab mats with an artistUrl) a secondary call-to-
  // action linking to the artist's Patreon for the physical print.
  renderPlaymatSidebar(pack) {
    if (!pack) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-center px-6" style={{ color: TEXT_MUTED }}>
          <div className="text-[0.8125cqw] italic">{t('Pick a playmat to preview')}</div>
        </div>
      );
    }
    const { profile } = this.props;
    const { owned, buying } = this.state;
    const starterPick = this.props.starterPick;
    const ownsThis = pack.cosmeticId ? owned.has(pack.cosmeticId) : false;
    const cost = pack.price?.coins || 0;
    const coins = profile?.coins || 0;
    const canAfford = coins >= cost;
    const url = getPlaymatPreviewUrl(pack, 'medium');
    const lore = pack.lore || pack.description || null;

    return (
      <div className="flex flex-col h-full min-h-0 gap-2.5">
        {/* Artwork. Medium-res variant, centered and height-capped so
            the lore + artist bio fit in the panel WITHOUT scrolling
            (the sidebar is never scrollable). Framed like the grid
            tiles so the owned stamp can hug the same top-left corner. */}
        <div className="shrink-0 flex justify-center">
          <div
            className="relative overflow-hidden"
            style={{
              height: '17cqw',
              aspectRatio: '14 / 10',
              maxWidth: '100%',
              borderRadius: 'var(--radius-lg)',
              border: `1px solid ${GOLD} 0.22)`,
              boxShadow: '0 8px 20px rgba(0,0,0,0.55), 0 0 0 1px rgba(0,0,0,0.45)',
            }}
          >
            <PlaymatPreview url={url} hovered={false} fill />
            {ownsThis ? (
              <div
                className="absolute top-2 left-2 px-2 py-0.5 text-[0.55cqw]"
                style={{ ...OWNED_STAMP_STYLE, zIndex: 20 }}
              >
                {t('Owned')}
              </div>
            ) : null}
          </div>
        </div>

        {/* Title */}
        <div className="shrink-0 text-center">
          <div className="text-base font-bold arena-heading" style={{ color: TEXT_PRIMARY, textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>
            {pack.name}
          </div>
          {/* Bundled mats (no artist credit) show their short flavour
              text as a subline directly under the name. Licensed mats
              keep their longer lore in the body below, next to the
              artist credit + "About the Artist." */}
          {!pack.artist && lore ? (
            <div className="text-[0.7cqw] italic mt-1.5 px-3" style={{ color: TEXT_BODY, lineHeight: 1.5 }}>
              {lore}
            </div>
          ) : null}
        </div>

        {/* Attribution. Gold seals for the house + the artist. Shown
            for licensed (protected) mats; the artist row appears once
            the catalog supplies artistName. */}
        {(pack.protected || pack.artistName) ? (
          <div className="shrink-0 flex items-center justify-center gap-4">
            {pack.protected && pack.artist ? this.renderArtistSeal(COLLECTOR_ARTHOUSE_SEAL, t('Presented by'), pack.artist, pack.artistUrl) : null}
            {pack.protected && pack.artistName ? (
              <div className="w-px h-8 shrink-0" style={{ background: `${GOLD} 0.22)` }} />
            ) : null}
            {pack.artistName ? this.renderArtistSeal(SEVERINE_SEAL, t('Artist'), pack.artistName, pack.artistHomeUrl || null) : null}
          </div>
        ) : null}

        {/* Lore + artist bio. The sidebar must never scroll, so this
            region takes the remaining height and AutoFitText scales the
            type down until the full lore + bio fit. The entire entry is
            always visible regardless of its length. */}
        {((lore && pack.artist) || pack.artistBio) ? (
          <AutoFitText
            className="flex-1 min-h-0 overflow-hidden px-1 flex flex-col justify-center"
            signature={`${pack.id}:${(lore || '').length}:${(pack.artistBio || '').length}`}
          >
            {(lore && pack.artist) ? (
              <div className="text-center px-2" style={{ fontSize: 'calc(0.7cqw * var(--fit-scale, 1))', color: TEXT_BODY, lineHeight: 1.55 }}>
                {lore}
              </div>
            ) : null}
            {pack.artistBio ? (
              <div className="pt-3" style={{ borderTop: `1px solid ${GOLD} 0.14)` }}>
                <div className="text-center mb-1.5 arena-heading" style={{ fontSize: 'calc(0.62cqw * var(--fit-scale, 1))', color: ACCENT_GOLD }}>
                  {t('About the Artist')}
                </div>
                <div className="text-center px-2" style={{ fontSize: 'calc(0.7cqw * var(--fit-scale, 1))', color: TEXT_BODY, lineHeight: 1.55 }}>
                  {pack.artistBio}
                </div>
              </div>
            ) : null}
          </AutoFitText>
        ) : <div className="flex-1 min-h-0" />}

        {/* Buy / support, pinned to the bottom. Playmats are single-
            entitlement: once owned, the ownership reads from the stamp
            on the preview above, so the Purchase button is hidden and
            only the Support-the-Artist link remains. */}
        <div className="shrink-0 flex flex-col gap-2.5">
          {starterPick ? (
            // Onboarding free pick: any mat, no cost. Selecting confirms.
            <ForgedButton
              tone="gold"
              disabled={buying || this.props.busy}
              onClick={() => this.props.onStarterPick?.(pack)}
              className="flex items-center justify-center gap-2.5 py-3 px-4 text-sm font-semibold arena-heading"
            >
              {(buying || this.props.busy) ? t('Choosing…') : t('Choose this playmat')}
            </ForgedButton>
          ) : !ownsThis ? (
            <ForgedButton
              tone="gold"
              disabled={!canAfford || buying}
              onClick={() => this.handleBuyPlaymat(pack)}
              className="flex items-center justify-center gap-2.5 py-3 px-4 text-sm font-semibold arena-heading"
            >
              {buying ? (
                t('Opening…')
              ) : !canAfford ? (
                t('Not enough gold')
              ) : (
                <>
                  <span>{t('Purchase')}</span>
                  <span className="inline-flex items-center gap-1 tabular-nums">
                    <CoinIcon size={13} />
                    {cost}
                  </span>
                </>
              )}
            </ForgedButton>
          ) : null}

          {/* Print-from-artist CTA. Only shown when the catalog entry
              has an artistUrl. Worded as a redirect ("from the artist")
              not a sales pitch. We don't profit from the print. Forged
              bronze plate so it shares the buttons' depth while staying
              visually secondary to the gold Purchase plate. */}
          {pack.artistUrl ? (
            <ForgedButton
              tone="bronze"
              href={pack.artistUrl}
              className="flex flex-col items-center justify-center text-center py-1 px-4"
            >
              <div className="inline-flex items-center gap-2 text-[0.95cqw] font-semibold arena-heading leading-tight">
                {t('Support the Artist')}
                <ExternalLink size={15} style={{ color: ACCENT_GOLD, flexShrink: 0 }} aria-hidden />
              </div>
              <div className="text-[0.75cqw] leading-tight" style={{ color: TEXT_MUTED }}>
                {t('Order the printed mat')}
              </div>
            </ForgedButton>
          ) : null}
        </div>
      </div>
    );
  }

  // The Dice tab sells through the rolling basket like portrait and
  // background packs: a set goes into the cart from its tile, the bar at
  // the foot of the view takes the purchase.
  renderDiceLayout(dicePacks) {
    const { owned, buying, cart } = this.state;
    return (
      <DiceSkinShelf
        packs={dicePacks}
        owned={owned}
        buying={buying}
        cart={cart}
        onAdd={(pack) => this.addToCart(pack.id)}
        onRemove={(pack) => this.setCart(pack.id, 0)}
      />
    );
  }

  renderPlaymatLayout(playmatPacks) {
    const { selectedPlaymatId } = this.state;
    const selectedPack = playmatPacks.find((p) => p.id === selectedPlaymatId) || playmatPacks[0] || null;
    return (
      <div className="flex gap-4 flex-1 min-h-0 px-6 py-4">
        {/* Left column: optional header (onboarding "Choose Your
            Playmat" title) above the grid, so the header lives over the
            grid only. The sidebar keeps the full panel height and reads
            identically to the store. */}
        <div className="flex-[62] min-h-0 flex flex-col">
          {this.props.starterHeader ? (
            <div className="shrink-0">{this.props.starterHeader}</div>
          ) : null}
          {/* `overflow-y: auto` makes the browser also clip overflow-x,
              which cuts the selection ring + hover lift off edge tiles.
              especially the top of the first row. The scroll area is
              padded on all sides by more than the ring's reach
              (outlineOffset 0.2cqw + width 0.18cqw) plus the 0.2cqw
              hover lift, so nothing is clipped. */}
          <div className="flex-1 min-h-0 overflow-y-auto px-3 py-3">
          <div
            className="grid gap-x-4 gap-y-5"
            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(15cqw, 1fr))' }}
          >
            {playmatPacks.map(this.renderPlaymatGridTile)}
          </div>
          </div>
        </div>

        {/* Right: Spellcraft-style preview sidebar */}
        <div className="flex-[38] min-h-0 overflow-hidden">
          <div
            className="relative p-4 h-full flex flex-col"
            style={{
              background: PANEL_BG,
              border: `1px solid ${GOLD} 0.2)`,
              borderRadius: 'var(--radius-lg)',
              isolation: 'isolate',
            }}
          >
            {/* Default plating: inherits the panel's corner radius and
                the -1px inset traces its outer stroke exactly. */}
            <FourCorners />
            <VikingOrnament ornament="style1007" variant="centerpiece" opacity={0.06} />
            {this.renderPlaymatSidebar(selectedPack)}
          </div>
        </div>
      </div>
    );
  }

  renderCartBar() {
    const { profile } = this.props;
    const { cart, packs, buying } = this.state;
    const { count, cost } = this.getCartTotal();
    const canAfford = (profile?.coins || 0) >= cost;
    const summary = count > 0
      ? packs
          .filter((p) => (cart[p.id] || 0) > 0)
          .map((p) => `${cart[p.id]}× ${p.name}`)
          .join(', ')
      : t('Click a pack or use +/− to add');
    return (
      <div className="w-full max-w-[56cqw] mx-auto shrink-0 px-6 pb-5">
        <div
          className="relative p-5 flex items-center justify-between"
          style={{
            background: PANEL_BG,
            backdropFilter: 'blur(8px)',
            border: `1px solid ${count > 0 ? `${GOLD} 0.35)` : `${GOLD} 0.15)`}`,
            borderRadius: 'var(--radius-lg)',
            boxShadow: count > 0 ? `0 0 20px ${GOLD} 0.08)` : 'none',
          }}
        >
          <CornerPlating position="top-left" />
          <CornerPlating position="top-right" />
          <CornerPlating position="bottom-left" />
          <CornerPlating position="bottom-right" />
          <div className="min-w-0">
            <div className="text-sm font-semibold" style={{ color: count > 0 ? '#e8d5a0' : 'rgba(166,160,155,0.4)' }}>
              {count > 0 ? (count === 1 ? t('1 pack selected') : t('{n} packs selected', { n: count })) : t('Nothing selected')}
            </div>
            <div className="text-xs mt-0.5 truncate max-w-[36cqw]" style={{ color: 'rgba(166,160,155,0.35)' }}>
              {summary}
            </div>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <div className="text-right">
              <div className="text-lg font-bold tabular-nums flex items-center justify-end gap-1.5" style={{ color: count > 0 ? '#f0d060' : 'rgba(166,160,155,0.2)' }}>
                {count > 0 ? <CoinIcon size={14} /> : null}
                {cost}
              </div>
              <div className="text-[0.625cqw]" style={{ color: 'rgba(166,160,155,0.3)' }}>{t('gold')}</div>
            </div>
            <button
              type="button"
              disabled={count === 0 || !canAfford || buying}
              className="px-8 py-3 text-sm font-semibold arena-heading transition-all hover:scale-[1.03] active:scale-[0.97] disabled:hover:scale-100"
              style={count > 0 && canAfford && !buying ? GOLD_BTN : { ...BEVELED_BTN, color: 'rgba(166,160,155,0.25)' }}
              onClick={() => this.setState({ showConfirm: true })}
            >
              {buying ? t('Opening…') : count === 0 ? t('Purchase') : canAfford ? t('Purchase') : t('Not enough gold')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  renderConfirmModal() {
    const { showConfirm, cart, packs } = this.state;
    if (!showConfirm) return null;
    const { count, cost } = this.getCartTotal();
    const lines = packs
      .map((p) => ({ pack: p, qty: cart[p.id] || 0 }))
      .filter((e) => e.qty > 0);
    // Playmats, tokens and dice skins are one fixed thing each: there is no
    // pack to open, so the button says what actually happens. Only a cart
    // holding a rolled pack (portrait, background) gets the opening verb.
    const opensPacks = lines.some(
      ({ pack }) => pack.kind !== 'playmat' && pack.kind !== 'token' && pack.kind !== 'dice'
    );
    return (
      <div
        className="fixed inset-0 z-[60] flex items-center justify-center bg-black/75 backdrop-blur-sm"
        onClick={() => this.setState({ showConfirm: false })}
      >
        <div
          className="relative w-96 p-6"
          style={{
            background: PANEL_BG,
            backdropFilter: 'blur(8px)',
            border: `1px solid ${PANEL_BORDER}`,
            borderRadius: 'var(--radius-lg)',
            boxShadow: `0 0 40px rgba(0,0,0,0.5), 0 0 20px ${GOLD} 0.05)`,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <CornerPlating position="top-left" />
          <CornerPlating position="top-right" />
          <CornerPlating position="bottom-left" />
          <CornerPlating position="bottom-right" />
          <h2 className="text-lg font-bold arena-heading mb-4" style={{ color: '#e8d5a0' }}>{t('Confirm Purchase')}</h2>
          <div className="flex flex-col gap-2 mb-4">
            {lines.map(({ pack, qty }) => (
              <div key={pack.id} className="flex items-center justify-between text-sm py-1" style={{ borderBottom: `1px solid ${GOLD} 0.08)` }}>
                <span style={{ color: '#A6A09B' }}>{qty}× {pack.name}</span>
                <span className="tabular-nums font-semibold inline-flex items-center gap-1.5" style={{ color: '#f0d060' }}>
                  <CoinIcon size={12} />
                  {qty * (pack.price?.coins || 0)}
                </span>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between py-3 mb-5" style={{ borderTop: `1px solid ${GOLD} 0.15)` }}>
            <span className="text-sm font-semibold" style={{ color: '#e8d5a0' }}>{t('Total')}</span>
            <span className="text-lg font-bold tabular-nums inline-flex items-center gap-1.5" style={{ color: '#f0d060' }}>
              <CoinIcon size={14} />
              {cost}
            </span>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              className="flex-1 py-2.5 text-sm font-semibold transition-all hover:scale-[1.02] active:scale-[0.98]"
              style={{ ...BEVELED_BTN, background: `url("/tex-noise.webp"), url("/tex-stone.webp"), rgba(20, 16, 10, 0.95)`, borderRadius: 'var(--radius-sm)', color: '#A6A09B' }}
              onClick={() => this.setState({ showConfirm: false })}
            >
              {t('Cancel')}
            </button>
            <button
              type="button"
              className="flex-1 py-2.5 text-sm font-semibold arena-heading transition-all hover:scale-[1.02] active:scale-[0.97]"
              style={{ ...GOLD_BTN, borderRadius: 'var(--radius-sm)' }}
              onClick={this.handlePurchase}
            >
              {opensPacks ? (count === 1 ? t('Open 1 Pack') : t('Open {n} Packs', { n: count })) : t('Purchase')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  renderRevealModal() {
    return (
      <CosmeticRevealModal
        reveal={this.state.reveal}
        onClose={() => this.setState({ reveal: null })}
      />
    );
  }

  render() {
    const { loading, error, packs, subTab } = this.state;
    if (loading) {
      return (
        <div className="flex-1 flex items-center justify-center py-12">
          <RuneSpinner size={48} />
        </div>
      );
    }
    if (error) {
      return (
        <div className="flex-1 flex flex-col items-center justify-center gap-3 py-16 text-center">
          <div className="text-[0.8125cqw]" style={{ color: '#e07060' }}>{error}</div>
          <button
            type="button"
            className="px-4 py-2 text-[0.6875cqw]"
            style={{ ...BEVELED_BTN, color: TEXT_BODY, borderRadius: 'var(--radius-md)' }}
            onClick={this.load}
          >
            {t('Retry')}
          </button>
        </div>
      );
    }
    if (packs.length === 0) {
      return (
        <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center py-16">
          <div className="text-[0.8125cqw] italic" style={{ color: TEXT_MUTED }}>
            {t('No cosmetic packs available yet.')}
          </div>
        </div>
      );
    }

    const tokenPacks = packs.filter((p) => p.kind === 'token');
    const dicePacks = packs.filter((p) => p.kind === 'dice');
    // `portraitPacks` is the catch-all bucket, so every new kind has to be
    // subtracted from it by hand or it lands in Portrait Packs wearing the
    // wrong preview component.
    const portraitPacks = packs.filter((p) => p.kind === 'portrait' || (p.kind !== 'background' && p.kind !== 'playmat' && p.kind !== 'token' && p.kind !== 'dice'));
    const backgroundPacks = packs.filter((p) => p.kind === 'background');
    const playmatPacks = packs
      .filter((p) => p.kind === 'playmat')
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    const visiblePacks = subTab === 'background' ? backgroundPacks
      : subTab === 'playmat' ? playmatPacks
      : subTab === 'token' ? tokenPacks
      : subTab === 'dice' ? dicePacks
      : portraitPacks;
    const isWideTab = subTab === 'background';

    // Onboarding "first playmat" mode: just the playmat grid + sidebar,
    // no sub-tabs, no cart, no buy/reveal modals. The sidebar's "Choose
    // this playmat" button is the confirm (handled by onStarterPick).
    if (this.props.starterPick) {
      // Include the free Vanilla mat alongside the server playmat packs.
      const starterPlaymatPacks = [STARTER_VANILLA_PACK, ...playmatPacks]
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      return (
        <div className="flex flex-col flex-1 min-h-0">
          {this.renderPlaymatLayout(starterPlaymatPacks)}
        </div>
      );
    }

    return (
      <div className="flex flex-col flex-1 min-h-0">
        {this.renderSubTabs(portraitPacks, backgroundPacks, playmatPacks, tokenPacks, dicePacks)}

        {subTab === 'dice' ? (
          this.renderDiceLayout(dicePacks)
        ) : subTab === 'playmat' ? (
          this.renderPlaymatLayout(playmatPacks)
        ) : (
          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
            <div
              className="grid gap-x-4 gap-y-6 mx-auto"
              style={{
                // Landscape Realm tiles are wider than portrait tiles,
                // so they need a wider column minimum. Both keep
                // auto-fill so rows break naturally on narrow canvases.
                gridTemplateColumns: isWideTab
                  ? 'repeat(auto-fill, minmax(20cqw, 1fr))'
                  : 'repeat(auto-fill, minmax(14cqw, 1fr))',
                maxWidth: isWideTab ? '84cqw' : '72cqw',
              }}
            >
              {visiblePacks.map(this.renderPackTile)}
            </div>
          </div>
        )}

        {/* Playmat sub-tab has its own per-mat Purchase button in the
            sidebar, so the rolling-cart footer is redundant there and
            crowds the layout. Other sub-tabs still need it (multi-
            pack rolling purchases). */}
        {subTab !== 'playmat' ? this.renderCartBar() : null}
        {this.renderConfirmModal()}
        {this.renderRevealModal()}
      </div>
    );
  }
}
